// src/components/layout/DashboardLayout.js 
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  Home, Users, Calendar, DollarSign, User, 
  Stethoscope, FileText, CreditCard, 
  Pill, Ambulance, ShoppingBag, Package,
  ClipboardCheck, Truck, TrendingUp, BarChart3,
  Shield, Settings, Database, Building2, UserCog,
  Car, MapPin
} from 'lucide-react';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  // Admin menu items (shared by both admin and superadmin)
  const adminMenuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: Home },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/admins', label: 'Admin Management', icon: UserCog },
    { path: '/admin/hospitals', label: 'Hospitals', icon: Building2 },
    { path: '/admin/pharmacies', label: 'Pharmacies', icon: Package },
    { path: '/admin/ambulances', label: 'Ambulances', icon: Ambulance },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/reports', label: 'Reports', icon: FileText },
    { path: '/admin/payments', label: 'Payments', icon: CreditCard },
    { path: '/admin/content', label: 'Content', icon: Database },
    { path: '/admin/settings', label: 'System Settings', icon: Settings },
    { path: '/admin/profile', label: 'My Profile', icon: User },
  ];

  const menuItems = {
    admin: adminMenuItems,
    superadmin: adminMenuItems,
    hospital: [
      { path: '/hospital', label: 'Dashboard', icon: Home },
      { path: '/hospital/services', label: 'Services', icon: Stethoscope },
      { path: '/hospital/doctors', label: 'Doctors', icon: Users },
      { path: '/hospital/ambulances', label: 'Ambulances', icon: Ambulance },
      { path: '/hospital/appointments', label: 'Appointments', icon: Calendar },
      { path: '/hospital/admissions', label: 'Admissions', icon: FileText },
      { path: '/hospital/inventory', label: 'Inventory', icon: FileText },
      { path: '/hospital/reports', label: 'Reports', icon: FileText },
      { path: '/hospital/profile', label: 'Profile', icon: User },
    ],
    doctor: [
      { path: '/doctor', label: 'Dashboard', icon: Home },
      { path: '/doctor/consultations', label: 'Consultations', icon: Stethoscope },
      { path: '/doctor/patients', label: 'Patient Records', icon: Users },
      { path: '/doctor/schedule', label: 'Schedule', icon: Calendar },
      { path: '/doctor/earnings', label: 'Earnings', icon: DollarSign },
      { path: '/doctor/subscription', label: 'Subscription', icon: CreditCard },
      { path: '/doctor/profile', label: 'Profile', icon: User },
    ],
    patient: [
      { path: '/patient', label: 'Dashboard', icon: Home },
      { path: '/patient/consultation/book', label: 'Book Consultation', icon: Calendar },
      { path: '/patient/consultations', label: 'My Consultations', icon: Stethoscope },
      { path: '/patient/dependents', label: 'My Dependents', icon: Users },
      { path: '/patient/prescriptions', label: 'Prescriptions', icon: FileText },
      { path: '/patient/medicine/order', label: 'Order Medicine', icon: Pill },
      { path: '/patient/orders', label: 'My Orders', icon: ShoppingBag },
      { path: '/patient/emergency', label: 'Emergency Services', icon: Ambulance },
      { path: '/patient/medical-history', label: 'Medical History', icon: FileText },
      { path: '/patient/profile', label: 'Profile', icon: User },
    ],
    pharmacy: [
      { path: '/pharmacy', label: 'Dashboard', icon: Home },
      { path: '/pharmacy/inventory', label: 'Manage Inventory', icon: Package },
      { path: '/pharmacy/orders', label: 'Manage Orders', icon: ShoppingBag },
      { path: '/pharmacy/prescriptions', label: 'Verify Prescriptions', icon: ClipboardCheck },
      { path: '/pharmacy/delivery', label: 'Delivery Management', icon: Truck },
      { path: '/pharmacy/sales', label: 'Sales Report', icon: TrendingUp },
      { path: '/pharmacy/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/pharmacy/profile', label: 'Profile', icon: User },
      { path: '/pharmacy/subscription', label: 'Subscription', icon: CreditCard },
    ],
    ambulance: [
      { path: '/ambulance', label: 'Dashboard', icon: Home },
      { path: '/ambulance/requests', label: 'Service Requests', icon: Ambulance },
      { path: '/ambulance/vehicles', label: 'Manage Vehicles', icon: Car },
      { path: '/ambulance/drivers', label: 'Manage Drivers', icon: Users },
      { path: '/ambulance/tracking', label: 'Live Tracking', icon: MapPin },
      { path: '/ambulance/analytics', label: 'Analytics', icon: BarChart3 },
      { path: '/ambulance/earnings', label: 'Earnings', icon: DollarSign },
      { path: '/ambulance/subscription', label: 'Subscription', icon: CreditCard },
      { path: '/ambulance/profile', label: 'Profile', icon: User },
    ],
  };

  const currentMenuItems = menuItems[user?.role] || [];

  const getRoleColor = (role) => {
    const colors = {
      admin: 'indigo',
      superadmin: 'indigo',
      hospital: 'blue',
      doctor: 'green',
      patient: 'purple',
      pharmacy: 'orange',
      ambulance: 'red'
    };
    return colors[role] || 'blue';
  };

  const getRoleDisplayName = (role) => {
    if (role === 'superadmin') return 'Super Admin';
    return role?.charAt(0).toUpperCase() + role?.slice(1);
  };

  const roleColor = getRoleColor(user?.role);
  const isAdminRole = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="flex h-screen bg-gray-100">
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-16'
        } bg-white shadow-lg transition-all duration-300 flex flex-col`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            {isAdminRole && (
              <Shield className={`w-6 h-6 text-${roleColor}-600`} />
            )}
            {user?.role === 'ambulance' && (
              <Ambulance className={`w-6 h-6 text-${roleColor}-600`} />
            )}
            <h2 className={`font-bold text-xl text-${roleColor}-600 ${!sidebarOpen && 'hidden'}`}>
              MediGo
            </h2>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? '←' : '→'}
          </button>
        </div>

        {sidebarOpen && user?.role && (
          <div className={`mx-4 mt-4 mb-2 px-3 py-2 bg-${roleColor}-50 border border-${roleColor}-200 rounded-lg`}>
            <div className="flex items-center space-x-2">
              {user.role === 'ambulance' ? (
                <Ambulance className={`w-4 h-4 text-${roleColor}-600`} />
              ) : isAdminRole ? (
                <Shield className={`w-4 h-4 text-${roleColor}-600`} />
              ) : null}
              <span className={`text-sm font-medium text-${roleColor}-700`}>
                {getRoleDisplayName(user.role)} Portal
              </span>
            </div>
          </div>
        )}

        <nav className="mt-2 flex-1 overflow-y-auto">
          {currentMenuItems.length > 0 ? (
            currentMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-3 text-gray-700 hover:bg-${roleColor}-50 hover:text-${roleColor}-600 transition-colors ${
                    isActive ? `bg-${roleColor}-50 text-${roleColor}-600 border-r-4 border-${roleColor}-600` : ''
                  }`}
                  title={sidebarOpen ? '' : item.label}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="ml-3">{item.label}</span>
                  )}
                </Link>
              );
            })
          ) : (
            <div className="px-4 py-3 text-gray-500 text-sm">
              No menu items available
            </div>
          )}
        </nav>

        {user && (
          <div className="border-t p-4">
            {sidebarOpen ? (
              <Link to={`/${user.role}/profile`} className="block">
                <div className="flex items-center space-x-3 hover:bg-gray-50 p-2 rounded-lg transition-colors">
                  <div className={`w-10 h-10 bg-${roleColor}-500 rounded-full flex items-center justify-center text-white font-semibold`}>
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                    </p>
                    <p className={`text-xs text-${roleColor}-600 font-medium`}>
                      {getRoleDisplayName(user.role)}
                    </p>
                  </div>
                </div>
              </Link>
            ) : (
              <Link to={`/${user.role}/profile`}>
                <div className={`w-10 h-10 bg-${roleColor}-500 rounded-full flex items-center justify-center text-white font-semibold mx-auto hover:opacity-80 transition-opacity`}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              </Link>
            )}
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-3">
              {isAdminRole && (
                <Shield className={`w-6 h-6 text-${roleColor}-600`} />
              )}
              {user?.role === 'ambulance' && (
                <Ambulance className={`w-6 h-6 text-${roleColor}-600`} />
              )}
              <h1 className="text-2xl font-semibold text-gray-800">
                {getRoleDisplayName(user?.role)} Portal
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-gray-600 hidden md:inline">
                Welcome, {user?.name || 'User'}
              </span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;